/**
 * Weather conditions: the one table that turns a forecast code into what the
 * site shows — a label and the kind of icon to draw beside it.
 *
 * It used to map codes to emoji, copied into three files. Emoji are not a
 * reliable way to draw an icon: most weather emoji (🌤 🌫 🌦 🌧 🌨 ⛈) default
 * to plain-text presentation, our fonts have no glyph for them, and on
 * browsers without a matching fallback font (Firefox on Windows especially)
 * they rendered as a boxed hex code like "01F 324". Now the data carries a
 * `kind`, and components/WeatherIcon.tsx draws it as SVG, identically
 * everywhere.
 */

export type WeatherKind =
  | "clear"
  | "mostly-clear"
  | "partly-cloudy"
  | "overcast"
  | "fog"
  | "drizzle"
  | "rain"
  | "snow"
  | "storm"
  | "unknown";

export const WEATHER_KINDS: readonly WeatherKind[] = [
  "clear",
  "mostly-clear",
  "partly-cloudy",
  "overcast",
  "fog",
  "drizzle",
  "rain",
  "snow",
  "storm",
  "unknown",
];

export type ConditionEntry = readonly [label: string, kind: WeatherKind];

export const UNKNOWN_CONDITION: ConditionEntry = ["—", "unknown"];

/** Open-Meteo WMO weather codes. */
export const WMO_CONDITIONS: Record<number, ConditionEntry> = {
  0: ["Clear", "clear"],
  1: ["Mainly clear", "mostly-clear"],
  2: ["Partly cloudy", "partly-cloudy"],
  3: ["Overcast", "overcast"],
  45: ["Fog", "fog"],
  48: ["Fog", "fog"],
  51: ["Light drizzle", "drizzle"],
  53: ["Drizzle", "drizzle"],
  55: ["Drizzle", "drizzle"],
  61: ["Light rain", "rain"],
  63: ["Rain", "rain"],
  65: ["Heavy rain", "rain"],
  71: ["Light snow", "snow"],
  73: ["Snow", "snow"],
  75: ["Heavy snow", "snow"],
  77: ["Snow grains", "snow"],
  80: ["Showers", "rain"],
  81: ["Showers", "rain"],
  82: ["Heavy showers", "rain"],
  85: ["Snow showers", "snow"],
  86: ["Snow showers", "snow"],
  95: ["Thunderstorm", "storm"],
  96: ["Thunderstorm", "storm"],
  99: ["Thunderstorm", "storm"],
};

/**
 * MET Norway symbol codes, base form (no _day/_night suffix). The fallback
 * source when Open-Meteo is rate-limited.
 */
export const MET_CONDITIONS: Record<string, ConditionEntry> = {
  clearsky: ["Clear", "clear"],
  fair: ["Mainly clear", "mostly-clear"],
  partlycloudy: ["Partly cloudy", "partly-cloudy"],
  cloudy: ["Overcast", "overcast"],
  fog: ["Fog", "fog"],
  lightrainshowers: ["Light showers", "rain"],
  rainshowers: ["Showers", "rain"],
  heavyrainshowers: ["Heavy showers", "rain"],
  lightrain: ["Light rain", "rain"],
  rain: ["Rain", "rain"],
  heavyrain: ["Heavy rain", "rain"],
  lightsleet: ["Sleet", "snow"],
  sleet: ["Sleet", "snow"],
  heavysleet: ["Heavy sleet", "snow"],
  lightsnow: ["Light snow", "snow"],
  snow: ["Snow", "snow"],
  heavysnow: ["Heavy snow", "snow"],
  lightsnowshowers: ["Snow showers", "snow"],
  snowshowers: ["Snow showers", "snow"],
  lightrainshowersandthunder: ["Thunderstorm", "storm"],
  rainandthunder: ["Thunderstorm", "storm"],
  heavyrainandthunder: ["Thunderstorm", "storm"],
};

export function metSymbolCondition(symbol: string | undefined): ConditionEntry {
  if (!symbol) return UNKNOWN_CONDITION;
  const base = symbol.replace(/_(day|night|polartwilight)$/, "");
  return MET_CONDITIONS[base] ?? UNKNOWN_CONDITION;
}

/**
 * The sky when nothing is falling, judged by cloud cover rather than the WMO
 * code — forecast models emit storm codes for mere instability on clear days.
 */
export function cloudCoverCondition(cloudCover: number): ConditionEntry {
  if (cloudCover < 20) return ["Clear", "clear"];
  if (cloudCover < 45) return ["Mainly clear", "mostly-clear"];
  if (cloudCover < 70) return ["Partly cloudy", "partly-cloudy"];
  return ["Overcast", "overcast"];
}

/**
 * The icon kind for a weather reading.
 *
 * Trusts `kind` when it is one we know. Otherwise works it out from the label,
 * which every reading has: /api/weather responses are cached for up to half an
 * hour, so for a while after this ships browsers still receive the old shape,
 * with an emoji `icon` and no `kind`.
 */
export function weatherKindOf(wx: { kind?: unknown; label?: unknown } | null | undefined): WeatherKind {
  if (!wx) return "unknown";
  if (typeof wx.kind === "string" && (WEATHER_KINDS as readonly string[]).includes(wx.kind)) {
    return wx.kind as WeatherKind;
  }
  const label = typeof wx.label === "string" ? wx.label.toLowerCase() : "";
  if (label.includes("thunder")) return "storm";
  if (label.includes("snow") || label.includes("sleet")) return "snow";
  if (label.includes("drizzle")) return "drizzle";
  if (label.includes("rain") || label.includes("shower")) return "rain";
  if (label.includes("fog") || label.includes("mist")) return "fog";
  if (label.includes("overcast")) return "overcast";
  if (label.includes("partly")) return "partly-cloudy";
  if (label.includes("mainly")) return "mostly-clear";
  if (label.includes("clear")) return "clear";
  return "unknown";
}
